import React from "react";
import { Card, CardBody, CardHeader } from "reactstrap";

/**
 * Section wrapper for grouping related fields inside a form. Same
 * visual language as SectionCard but tuned for form pages — uses a
 * slightly heavier title weight, an optional numbered step prefix
 * (e.g. "1. Job Details"), and consistent body padding.
 *
 * Pure presentation. Validation, state, and submit handling stay in
 * the parent form. Do not couple business logic to this component.
 *
 * Props
 *  - icon:        ri-* icon (rendered in the soft-disc on the left)
 *  - step:        optional number — renders "1." prefix on the title
 *  - title:       section title (required if you want a header)
 *  - subtitle:    helper line under the title
 *  - required:    bool — when true, marks the whole section as required
 *                 with a subtle red dot next to the title
 *  - actions:     React node — buttons rendered on the right (e.g.
 *                 "Reset section", "Toggle expanded")
 *  - children:    form fields
 *  - className:   extra wrapper className
 *  - bodyClassName: extra className on CardBody
 *  - collapsed:   bool — when true, hides the body (consumer controls)
 *  - tone:        "primary" | "info" | "success" | "warning" | "danger" |
 *                 "secondary" — controls the icon disc color
 */
const TONE_HEX = {
  primary: "#405189",
  info: "#299cdb",
  success: "#0ab39c",
  warning: "#f7b84b",
  danger: "#f06548",
  secondary: "#74788d",
};

const FormSectionCard = ({
  icon,
  step,
  title,
  subtitle,
  required = false,
  actions,
  children,
  className = "",
  bodyClassName = "",
  collapsed = false,
  tone = "primary",
}) => {
  const hex = TONE_HEX[tone] || TONE_HEX.primary;
  const hasHeader = title || actions || icon;

  return (
    <Card className={`shadow-sm border-0 mb-3 ${className}`}>
      {hasHeader ? (
        <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div
              className="d-flex align-items-center gap-2"
              style={{ minWidth: 0 }}
            >
              {icon ? (
                <span
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: `${hex}1A`,
                    color: hex,
                    flexShrink: 0,
                  }}
                >
                  <i className={icon} style={{ fontSize: 16 }} />
                </span>
              ) : null}
              <div style={{ minWidth: 0 }}>
                {title ? (
                  <h6
                    className="fw-semibold mb-0 d-flex align-items-center gap-2"
                    style={{ color: "#212529" }}
                  >
                    {typeof step === "number" ? (
                      <span
                        className="text-muted"
                        style={{ fontWeight: 500, fontSize: 14 }}
                      >
                        {step}.
                      </span>
                    ) : null}
                    <span>{title}</span>
                    {required ? (
                      <span
                        aria-label="required section"
                        title="Required section"
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: TONE_HEX.danger,
                          display: "inline-block",
                        }}
                      />
                    ) : null}
                  </h6>
                ) : null}
                {subtitle ? (
                  <p
                    className="text-muted small mb-0"
                    style={{ fontSize: 12 }}
                  >
                    {subtitle}
                  </p>
                ) : null}
              </div>
            </div>
            {actions ? (
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {actions}
              </div>
            ) : null}
          </div>
        </CardHeader>
      ) : null}

      {!collapsed ? (
        <CardBody className={`pt-3 ${bodyClassName}`}>
          {children}
        </CardBody>
      ) : null}
    </Card>
  );
};

export default FormSectionCard;
